import {PromisePool} from "@supercharge/promise-pool";
import {damsFetch} from "../app/damsFetch";
import decamelizeKeysDeep from "decamelize-keys-deep";
import {clientLog} from "../clientLog";
import {createTagReference} from "./createTagReference";

/**
 * Saves new tags to the DAMS database, if required, and returns the updated tags.
 *
 * @param {Object} user - The user object.
 * @param {number} userId - The ID of the user.
 * @param {string} collectionId - The ID of the collection.
 * @param {Array<Object>} tags - The array of tags. For new tags, their actual title is stored under the property "inputValue".
 * @param {string} [tagType = 'foldertag'] - The type of tag.
 * @param {string} [tagPrefix = ''] - A prefix to be added to the tag type when creating new publishing tags.
 * @returns {Promise<Array<Object>>} - The updated array of tags.
 */
export const saveNewTags = async (user,
                                  userId,
                                  collectionId,
                                  tags,
                                  tagType = 'foldertag',
                                  tagPrefix = '') => {

    /**
     * Saves the documents in chunks to the DAMS database, if required.
     * @param {Array<Object>} documents - The array of documents to be saved.
     * @returns {Promise<Array<Object>>} - The array of documents saved.
     */
    const postDocumentsInChunks = async (documents) => {
        return await PromisePool
            .withConcurrency(2)
            .for(documents)
            .process(async (document) => {
                try {
                    const res = await damsFetch('/documents', {
                        method: 'POST',
                        body: JSON.stringify(decamelizeKeysDeep(document))
                    });

                    const {id, uniqueId, locale, title, source, content, documentType} = res;
                    return createTagReference(user, userId, id, uniqueId, locale, title, source, content, documentType);
                } catch (e) {
                    clientLog('error', e, 'savenewtags');
                }
            });
    };

    /**
     * Creates a document object for the DAMS database, used when saving tags.
     * @param {{title: string}} u - The tag object.
     * @returns {{collectionId: number, schemaId: number, locale: string, _action: string, title: string, status: string, source: string, sourceId: null, documentType: string, content: {name: string, title: string}}} - The document object.
     */
    const createDamsDocumentObject = (u) => {
        const {title, publishingType = ''} = u;
        if (!title || title.trim() === '') {
            throw new Error(`Cannot create a tag document with an empty title (tag: ${JSON.stringify(u)})`);
        }
        return {
            collectionId: collectionId,
            schemaId: 1,
            locale: "no",
            _action: "put",
            title: title,
            status: "published",
            source: 'dams',
            sourceId: null,
            documentType: tagPrefix !== '' ? `${tagPrefix}${publishingType}` : tagType,
            content: {
                name: title,
                title: title
            }
        };
    }

    /**
     * Creates an array of document objects for the DAMS database, used when saving tags.
     * @param {Array<{title: string}>} unsaved - The array of tags that does not exist in the DAMS database.
     * @returns {Array<{collectionId: number, schemaId: number, locale: string, _action: string, title: string, status: string, source: string, sourceId: null, documentType: string, content: {name: string, title: string}}>} - The array of document objects.
     */
    const createDamsDocuments = (unsaved) => {
        return unsaved.map(u => {
            return createDamsDocumentObject(u);
        });
    };

    if (!collectionId || !tags || tags?.length === 0) {
        return;
    }

    // Get the list of tags that does not exist in the DAMS database.
    // NOTE: For new tags, their actual title is stored under the property "inputValue".
    const newTags = [...tags].filter(t => {
        return !t.uniqueId && t.inputValue && t.inputValue.trim() !== '';
    }).map(t => ({
        ...t,
        title: t.inputValue
    }));

    const documents = createDamsDocuments(newTags);
    return await postDocumentsInChunks(documents);
};